import { inject, injectable } from 'inversify';
import React from 'react';

import { useFlags } from '@project44/feature-flags';
import { User } from '@project44/ui-utils';

import { UserPrincipalUIService } from '../../../external/UserPrincipal/application/UserPrincipal.ui.service';
import { UserPrincipalUIController } from '../../../external/UserPrincipal/infrastructure/controllers/UserPrincipal.ui.controller';
import { ReactHookService } from '../../../shared/core/application/React.hook.service';
import { TUseToggleComponentHook } from '../../../shared/core/infrastructrue/types';
import { TUseSwitchTenantHook, TUseTenantQueryHook } from '../domain';
import { TenantHookFactory } from '../infrastructure/factories/Tenant.hook.factory';
import { TenantUIService } from './Tenant.ui.service';

@injectable()
export class TenantHookService {
  constructor(
    @inject(TenantHookFactory)
    private readonly tenantHookFactory: TenantHookFactory,

    @inject(ReactHookService)
    private readonly reactHookService: ReactHookService,

    @inject(UserPrincipalUIController)
    private readonly userPrincipalUIService: UserPrincipalUIService,

    @inject(TenantUIService)
    private readonly tenantUIService: TenantUIService,
  ) {}

  public composeUseTenantQueryHook(): TUseTenantQueryHook {
    return this.tenantHookFactory.composeUseTenantQueryHook();
  }

  public composeUseToggleTenantSwitcherComponent(payload: {
    user?: User;
    passThroughComponent?: React.ReactElement<unknown>;
  }): TUseToggleComponentHook {
    const { user, passThroughComponent } = payload;

    const ReactComponent = (props: {
      isToggleActive: boolean;
      toggleTenantSwitcherComponent: () => void;
    }) => {
      const { featureIam1057 } = useFlags();
      const { isToggleActive, toggleTenantSwitcherComponent } = props;

      if (typeof user === 'undefined') return null;

      return this.tenantUIService.createTenantSwitcherComponent({
        user,
        isInView: isToggleActive,
        toggleTenantSwitcherComponent,
        isFeatureFlagIam1057Enabled: featureIam1057,
        passThroughComponent:
          passThroughComponent ||
          this.userPrincipalUIService.createLogoutCardComponent({
            user,
            featureIam1057,
          }),
      });
    };

    return this.reactHookService.composeUseToggleComponentHook({
      ReactComponent,
    });
  }

  public composeUseSwitchTenantHook(): TUseSwitchTenantHook {
    return this.tenantHookFactory.composeUseSwitchTenantHook();
  }
}
